/**
 * Vera Global Chat — floating assistant button.
 *
 * Renders a fixed FAB in the bottom-right corner (industry standard:
 * Intercom, Zendesk, ChatGPT widget). When clicked, expands into a chat
 * panel. Only rendered when the featureVeraGlobalChat flag is enabled.
 *
 * Uses the existing GenericChatWindow for the message list and input.
 * Calls the /vera/global-chat backend endpoint via BackendService.
 */

import AutoAwesome from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Fab,
  IconButton,
  Paper,
  Slide,
  Typography,
} from '@mui/material';
import BackendService from 'BackendService';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  VERA_GLOBAL_STARTER_PROMPTS,
  VERA_PRIVACY_NOTE,
  VERA_PRIVACY_NOTE_SHORT,
} from '../constants/vera';
import { FeatureGate, FeatureType } from '../context/FeatureContext';
import { useFidesChat } from '../context/FidesChatContext';
import AIChatMessage from '../models/AIChatMessage';
import VeraGroundedMessage from './VeraGroundedMessage';
import { FloatingActionSlot } from './FloatingActionStack';
import GenericChatWindow from './GenericChatWindow';
import { Palette } from 'Palette';

const PANEL_WIDTH = 380;
const PANEL_HEIGHT = 580;
const EDGE_OFFSET = 24; // distance from viewport edge (px)

const PRESET_QUESTIONS = VERA_GLOBAL_STARTER_PROMPTS;

const VeraFloatingChatInner: React.FC = () => {
  const fidesChat = useFidesChat();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<AIChatMessage[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState<boolean | null>(null);
  const [isAcceptingDisclaimer, setIsAcceptingDisclaimer] = useState(false);
  const chatIdRef = useRef<string>(uuidv4());

  // Check disclaimer status when panel first opens
  useEffect(() => {
    if (open && disclaimerAccepted === null) {
      new BackendService()
        .getAiDisclaimerStatus()
        .then((result) => setDisclaimerAccepted(result.aiChatDisclaimerAccepted))
        .catch(() => setDisclaimerAccepted(false));
    }
  }, [open, disclaimerAccepted]);

  const acceptDisclaimer = async () => {
    setIsAcceptingDisclaimer(true);
    try {
      await new BackendService().acceptAiDisclaimer();
      setDisclaimerAccepted(true);
    } catch {
      // Allow proceeding — backend is best-effort for disclaimer recording
      setDisclaimerAccepted(true);
    } finally {
      setIsAcceptingDisclaimer(false);
    }
  };

  const toggle = () => setOpen((v) => !v);
  const close = () => setOpen(false);
  const hideFab = Boolean(fidesChat?.isPanelOpen);

  const sendMessage = useCallback(async (text: string) => {
    const userMsg: AIChatMessage = {
      role: 'user',
      parts: [{ text }],
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setIsProcessing(true);

    try {
      // Session history is stored in GCS on the backend; send only message + chatId (WAF-safe).
      const { reply, chatId, googleSearchGrounding } =
        await new BackendService().sendVeraGlobalMessage(text, [], chatIdRef.current);

      // Keep chatId stable across turns
      chatIdRef.current = chatId;

      const modelMsg: AIChatMessage = {
        role: 'model',
        parts: [{ text: reply }],
        timestamp: new Date(),
        googleSearchGrounding: googleSearchGrounding ?? null,
      };
      setMessages((prev) => [...prev, modelMsg]);
    } catch {
      const errorMsg: AIChatMessage = {
        role: 'model',
        parts: [{ text: 'Sorry, Vera is temporarily unavailable. Please try again in a moment.' }],
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const handlePreset = (prompt: string) => sendMessage(prompt);

  return (
    <>
      {/* Chat panel */}
      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          data-testid="vera-chat-panel"
          sx={{
            position: 'fixed',
            bottom: EDGE_OFFSET + 64 + 12, // above FAB
            right: EDGE_OFFSET,
            width: PANEL_WIDTH,
            height: PANEL_HEIGHT,
            maxHeight: `calc(100vh - ${EDGE_OFFSET + 64 + 12 + 16}px)`,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
            overflow: 'hidden',
            zIndex: 1300, // above most MUI components
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1.5,
              backgroundColor: Palette.default.buttonBg,
              color: '#fff',
              flexShrink: 0,
            }}
          >
            <AutoAwesome sx={{ fontSize: 18 }} />
            <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
              Ask Vera
            </Typography>
            <IconButton
              size="small"
              onClick={close}
              aria-label="Close Vera chat"
              sx={{ color: '#fff', p: 0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Disclaimer gate — shown until accepted */}
          {disclaimerAccepted === false && (
            <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="body2" fontWeight={600}>
                Before you chat with Vera
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                Vera is an AI data assistant. She can summarise your portfolio and explain fund
                information — but she does <strong>not</strong> provide investment advice or
                recommendations.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                Always speak with your Fides adviser before making investment decisions.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                {VERA_PRIVACY_NOTE}
              </Typography>
              <Button
                variant="contained"
                size="small"
                disabled={isAcceptingDisclaimer}
                onClick={acceptDisclaimer}
                sx={{ mt: 'auto', backgroundColor: Palette.default.buttonBg }}
                startIcon={
                  isAcceptingDisclaimer ? <CircularProgress size={14} color="inherit" /> : null
                }
              >
                I understand — start chatting
              </Button>
            </Box>
          )}

          {/* Loading disclaimer status */}
          {disclaimerAccepted === null && (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {/* Chat — only shown after disclaimer accepted */}
          {disclaimerAccepted === true && (
            <>
              {/* Preset prompt chips — shown only before first message */}
              {messages.length === 0 && (
                <Box
                  sx={{
                    px: 1.5,
                    pt: 1.5,
                    pb: 0.5,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.75,
                    flexShrink: 0,
                  }}
                >
                  {PRESET_QUESTIONS.map((q) => (
                    <Chip
                      key={q}
                      label={q}
                      size="small"
                      variant="outlined"
                      onClick={() => handlePreset(q)}
                      sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
                    />
                  ))}
                </Box>
              )}

              {/* Chat window */}
              <Box
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                  p: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <GenericChatWindow
                  messages={messages}
                  isProcessing={isProcessing}
                  onSubmit={sendMessage}
                  placeholderPrompt="Ask about your portfolio..."
                  messageAreaHeight="none"
                  renderModelContent={(msg) => (
                    <VeraGroundedMessage
                      text={msg.parts.map((part) => part.text).join('\n\n')}
                      grounding={msg.googleSearchGrounding}
                      markdownVariant="inline"
                    />
                  )}
                />
              </Box>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ px: 1.5, pb: 1, fontSize: '0.65rem', lineHeight: 1.4, flexShrink: 0 }}
              >
                {VERA_PRIVACY_NOTE_SHORT}
              </Typography>
            </>
          )}
        </Paper>
      </Slide>

      {/* FAB trigger — hidden while admin Fides chat panel is open (same corner).
          Rendered into the shared floating stack (rightmost slot) so it never
          overlaps the chat tray or other corner actions. */}
      {!hideFab && (
        <FloatingActionSlot order={30}>
          <Fab
            aria-label={open ? 'Close Vera chat' : 'Open Vera chat'}
            data-testid="vera-fab"
            onClick={toggle}
            sx={{
              backgroundColor: Palette.default.buttonBg,
              color: '#fff',
              '&:hover': { backgroundColor: Palette.default.text },
              boxShadow: open ? 2 : 6,
            }}
          >
            {open ? <CloseIcon /> : <AutoAwesome />}
          </Fab>
        </FloatingActionSlot>
      )}
    </>
  );
};

/** Drop this anywhere inside a FeatureProvider — renders nothing if the flag is off. */
const VeraFloatingChat: React.FC = () => (
  <FeatureGate feature={FeatureType.FeatureVeraGlobalChat}>
    <VeraFloatingChatInner />
  </FeatureGate>
);

export default VeraFloatingChat;
